/**
 * ProductCard Component
 * Shared product card used by all shop layouts
 */

import React, { useState } from 'react';
import { getPrimaryImageUrl } from '../../utils/imageUtils';
import { getStorefrontVariantAvailability } from '../../utils/storefrontVariantUtils';
import './ProductCard.css';

// Check if product has variable pricing (variants)
function hasVariablePricing(product) {
  // New variant matrix format
  if (product.variantMatrix?.variants?.length > 0) {
    return true;
  }
  if (product.variations && !Array.isArray(product.variations) && product.variations.variants?.length > 0) {
    return true;
  }
  // Legacy variations format
  if (product.variations?.length > 0 && product.variations.some(v => v.values?.length > 0)) {
    return true;
  }
  return false;
}

// Get price range for variable products
function getPriceRange(product) {
  let prices = [];

  // New variant matrix format
  if (product.variantMatrix?.variants?.length > 0) {
    prices = product.variantMatrix.variants
      .filter(v => v.price != null)
      .map(v => parseFloat(v.price));
  }
  else if (product.variations && !Array.isArray(product.variations) && product.variations.variants?.length > 0) {
    prices = product.variations.variants
      .filter(v => v.enabled !== false && v.price != null)
      .map(v => parseFloat(v.price));
  }
  // Legacy variations format
  else if (product.variations?.length > 0) {
    product.variations.forEach(variation => {
      if (variation.values?.length > 0) {
        variation.values.forEach(value => {
          if (value.price != null) {
            prices.push(parseFloat(value.price));
          }
        });
      }
    });
  }

  if (prices.length === 0) return null;

  return {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
}

function ProductCard({
  product,
  onClick,
  themeColors = {},
  formatPrice,
  cardStyle = 'standard',
  isNavigating = false,
  onAddToCart,
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const {
    primaryColor = '#2563eb',
    // Dark-mode-aware accent for price + CTA outline. Falls back to
    // primaryColor for callers that don't pass it (light-only contexts).
    priceCtaColor,
    borderColor = '#e5e7eb',
    textColor = '#111827',
    mutedColor = '#6b7280',
    bgAlt = '#f9fafb',
    ctaHoverBackground,
    ctaHoverText,
    accentColor = '#f59e0b',
    headingFont = 'inherit',
  } = themeColors;
  const effectivePriceCtaColor = priceCtaColor || primaryColor;
  const effectiveCtaHoverBackground = ctaHoverBackground || primaryColor;
  const effectiveCtaHoverText = ctaHoverText || '#ffffff';

  const imageUrl = getPrimaryImageUrl(product);
  const availability = getStorefrontVariantAvailability(product);
  const isOutOfStock = !availability.hasStock;
  const hasVariants = hasVariablePricing(product);
  const priceRange = hasVariants ? getPriceRange(product) : null;
  const canAddDirectly = !isOutOfStock && !hasVariants;
  const categoryLabel = product.category || product.productType || '';
  const isEditionCard = cardStyle === 'edition';
  const isAtelierCard = cardStyle === 'atelier';
  const isGalleryCard = cardStyle === 'gallery';

  // Format price display
  const renderPrice = () => {
    if (hasVariants && priceRange) {
      if (priceRange.min === priceRange.max) {
        return formatPrice(priceRange.min);
      }
      return `${formatPrice(priceRange.min)} - ${formatPrice(priceRange.max)}`;
    }
    return formatPrice(product.price);
  };

  const handleActionClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isOutOfStock) return;
    if (!canAddDirectly || !onAddToCart) {
      onClick(product);
      return;
    }
    onAddToCart(product);
  };

  return (
    <div
      className={`pc-card pc-card--${cardStyle} ${(!imageUrl || imageFailed) ? 'pc-card--without-image' : ''} ${isOutOfStock ? 'pc-card--out-of-stock' : ''}`}
      onClick={() => onClick(product)}
      style={{
        '--pc-primary': primaryColor,
        '--pc-border': borderColor,
        '--pc-text': textColor,
        '--pc-muted': mutedColor,
        '--pc-bg-alt': bgAlt,
        '--pc-accent': accentColor,
        cursor: isNavigating ? 'wait' : 'pointer',
      }}
    >
      <div className="pc-card__image-wrapper">
        {imageUrl && !imageFailed ? (
          <img
            src={imageUrl}
            alt={product.name}
            className={`pc-card__image ${isOutOfStock ? 'pc-card__image--faded' : ''}`}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="pc-card__placeholder">
            {isEditionCard || isAtelierCard || isGalleryCard ? <span>{categoryLabel || 'Product'}</span> : <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}
          </div>
        )}

        {/* Badges */}
        {isOutOfStock && (
          <span className="pc-card__badge pc-card__badge--out-of-stock">
            Out of Stock
          </span>
        )}
        {!isOutOfStock && product.featured && (
          <span className="pc-card__badge pc-card__badge--featured" style={{ backgroundColor: accentColor }}>
            Featured
          </span>
        )}
      </div>

      {/* Content */}
      <div className="pc-card__content">
        {categoryLabel && (
          <span className="pc-card__category">
            {categoryLabel}
          </span>
        )}
        <h3 className="pc-card__name" style={{ fontFamily: headingFont }}>
          {product.name}
        </h3>

        {cardStyle === 'detailed' && product.description && (
          <p className="pc-card__description">
            {product.description}
          </p>
        )}

        <div className="pc-card__spacer" />

        {/* Price */}
        <div className="pc-card__price-row">
          <span className="pc-card__price" style={{ color: effectivePriceCtaColor }}>
            {renderPrice()}
          </span>
          {!hasVariants && product.compareAtPrice && parseFloat(product.compareAtPrice) > parseFloat(product.price) && (
            <span className="pc-card__compare-price">
              {formatPrice(product.compareAtPrice)}
            </span>
          )}
        </div>

        {/* Action Button */}
        {cardStyle !== 'minimal' && (
          <button
            className={`pc-card__button ${isOutOfStock ? 'pc-card__button--disabled' : ''}`}
            style={{
              '--btn-color': effectivePriceCtaColor,
              '--btn-hover-bg': effectiveCtaHoverBackground,
              '--btn-hover-text': effectiveCtaHoverText,
            }}
            disabled={isOutOfStock}
            onClick={handleActionClick}
            type="button"
          >
            {isOutOfStock ? (
              'Out of Stock'
            ) : hasVariants ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                Choose Options
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="9" cy="21" r="1"/>
                  <circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
                Add to Cart
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default ProductCard;
